/**
 * Landing Page Template — model + built-in templates.
 *
 * A landing page is published EXACTLY like any self-service dashboard (a
 * `dashboard_layouts` row whose `layout_json` the published view renders), but
 * its layout_json carries `dashboardType: 'landing-page'` plus a `landing`
 * config block instead of `pageContent` widgets. That keeps every existing
 * pipe working for free: the sidenav/topnav "Dashboards" menu lists it, the
 * `/dvm-published/<slug>` route serves it (so `LANDING_ROUTE` in the
 * environment files can point at it), share links work, and republish updates
 * the same backend row.
 */

/** `layout_json.dashboardType` value that marks a landing page. */
export const LANDING_PAGE_DASHBOARD_TYPE = 'landing-page';

export interface LandingModuleLink {
  id: string;
  label: string;
  /** 'dashboard' → a published dashboard slug; 'route' → an in-app path;
   *  'url' → an external link opened in a new tab. */
  targetType: 'dashboard' | 'route' | 'url';
  target: string;
  /** 'primary' = filled accent button; 'outline' = bordered list button. */
  style?: 'primary' | 'outline';
  /** Sub-menu items — when present (non-empty) this button renders as an
   *  expandable GROUP (chevron toggle, own `target` ignored) and the children
   *  are the navigable entries. ONE level deep only: children's `children`
   *  are ignored. */
  children?: LandingModuleLink[];
}

export interface LandingModule {
  id: string;
  /** Small accent kicker above the title, e.g. "MODULE 01". */
  badge: string;
  /** Material icon name shown beside the badge. */
  icon: string;
  title: string;
  description: string;
  /** Per-module accent colour; falls back to the page accent. */
  accent?: string;
  /** Card width in the grid: undefined = normal (1 column), 'wide' = spans 2
   *  columns, 'full' = spans the whole row. Collapses back to 1 column on
   *  narrow screens (see the renderer's media query). */
  size?: 'wide' | 'full';
  /** Minimum card height in px (undefined = auto/content height). The card
   *  still grows past this when its content needs more room. Set by the
   *  Edit-Layout vertical drag or the editor's "Min height" field. */
  minHeight?: number;
  links: LandingModuleLink[];
}

export interface LandingPageConfig {
  header: {
    /** Material icon name for the page logo chip. */
    icon?: string;
    title: string;
    subtitle?: string;
  };
  /** Small-caps label above the module grid, e.g. "SELECT MODULE". */
  sectionLabel?: string;
  /** Page-level accent colour (CSS color). */
  accent?: string;
  modules: LandingModule[];
}

export interface LandingPageTemplate {
  id: string;
  name: string;
  description?: string;
  config: LandingPageConfig;
}

/** Same regex as SelfServiceDashboardService.slugify / the sidenav's
 *  nameToSlug — MUST stay byte-for-byte identical so a landing page's menu
 *  link, published URL and backend lookup all resolve the same record. */
export function landingSlugify(name: string): string {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/** True when a published layout_json is a landing page. */
export function isLandingPageLayout(layoutJson: unknown): boolean {
  return !!layoutJson
    && typeof layoutJson === 'object'
    && String((layoutJson as Record<string, unknown>)['dashboardType'] ?? '') === LANDING_PAGE_DASHBOARD_TYPE;
}

/** Pull the `landing` block out of a layout_json, normalising every field so
 *  a partial/older record still renders instead of throwing on undefined. */
export function extractLandingConfig(layoutJson: Record<string, unknown>): LandingPageConfig {
  const raw = (layoutJson?.['landing'] ?? {}) as Record<string, any>;
  const header = (raw['header'] ?? {}) as Record<string, any>;
  const modules = Array.isArray(raw['modules']) ? raw['modules'] : [];
  return {
    header: {
      icon: String(header['icon'] ?? '') || undefined,
      title: String(header['title'] ?? layoutJson?.['dashboard-name'] ?? layoutJson?.['name'] ?? 'Landing Page'),
      subtitle: String(header['subtitle'] ?? '') || undefined,
    },
    sectionLabel: String(raw['sectionLabel'] ?? '') || undefined,
    accent: String(raw['accent'] ?? '') || undefined,
    modules: modules.map((m: any, index: number) => ({
      id: String(m?.id ?? `module-${index}`),
      badge: String(m?.badge ?? ''),
      icon: String(m?.icon ?? 'dashboard'),
      title: String(m?.title ?? ''),
      description: String(m?.description ?? ''),
      accent: String(m?.accent ?? '') || undefined,
      size: m?.size === 'wide' || m?.size === 'full' ? m.size : undefined,
      minHeight: Number.isFinite(Number(m?.minHeight)) && Number(m?.minHeight) > 0
        ? Math.round(Number(m.minHeight))
        : undefined,
      links: sanitizeLinks(m?.links, `link-${index}`, true),
    })),
  };
}

/** Normalise a raw links array; `allowChildren` limits sub-menus to ONE level. */
function sanitizeLinks(raw: unknown, idPrefix: string, allowChildren: boolean): LandingModuleLink[] {
  return (Array.isArray(raw) ? raw : []).map((l: any, li: number) => {
    const children = allowChildren ? sanitizeLinks(l?.children, `${idPrefix}-${li}`, false) : [];
    return {
      id: String(l?.id ?? `${idPrefix}-${li}`),
      label: String(l?.label ?? ''),
      // 'environment-url' is a retired targetType from older published pages —
      // treat it as 'url' so those buttons keep redirecting via
      // LANDING_PAGE_EXTERNAL_BASE_URL instead of silently becoming in-app routes.
      targetType: l?.targetType === 'url'
        || l?.targetType === 'route'
        ? l.targetType
        : l?.targetType === 'environment-url'
        ? 'url'
        : 'dashboard',
      target: String(l?.target ?? ''),
      style: l?.style === 'primary' ? 'primary' : 'outline',
      ...(children.length ? { children } : {}),
    };
  });
}

/** The template Vinay demoed (IMPACT-style module chooser) — the initial
 *  predefined template. More can be added without code changes via the
 *  `assets/landing-page-templates/templates.json` asset (asset entries with a
 *  matching `id` override these built-ins). */
export const BUILT_IN_LANDING_TEMPLATES: LandingPageTemplate[] = [
  // IMPACT-style template temporarily hidden from the picker — uncomment to
  // bring it back (or ship a copy via the templates.json asset instead).
  // {
  //   id: 'impact-modules',
  //   name: 'Module Chooser (IMPACT style)',
  //   description: 'A welcome header plus a grid of module cards, each with its own action buttons — like the IMPACT pricing tool home page.',
  //   config: {
  //     header: {
  //       icon: 'star',
  //       title: 'IMPACT (Industrial Markets Pricing and Analytics Collaboration Tool)',
  //       subtitle: 'Welcome to IMPACT. Choose a module to plan price changes, execute pricing actions, or analyze key trends across costs, sales, market prices, PPI, and more.',
  //     },
  //     sectionLabel: 'SELECT MODULE',
  //     accent: '#c8102e',
  //     modules: [
  //       {
  //         id: 'module-planning',
  //         badge: 'MODULE 01',
  //         icon: 'edit_calendar',
  //         title: 'Planning',
  //         description: 'Plan and simulate price changes for AOP',
  //         links: [
  //           { id: 'planning-start', label: 'Start Planning', targetType: 'dashboard', target: '', style: 'outline' },
  //           { id: 'planning-apa', label: 'APA Planning framework', targetType: 'dashboard', target: '', style: 'outline' },
  //           { id: 'planning-account', label: 'Account planning module', targetType: 'dashboard', target: '', style: 'outline' },
  //           { id: 'planning-controller', label: 'Controller module', targetType: 'dashboard', target: '', style: 'outline' },
  //         ],
  //       },
  //       {
  //         id: 'module-pricing',
  //         badge: 'MODULE 02',
  //         icon: 'trending_up',
  //         title: 'Pricing',
  //         description: 'Execute pricing actions across products, including quote-level updates and approvals.',
  //         links: [
  //           { id: 'pricing-explore', label: 'Explore Pricing', targetType: 'dashboard', target: '', style: 'outline' },
  //         ],
  //       },
  //       {
  //         id: 'module-analytics',
  //         badge: 'MODULE 03',
  //         icon: 'query_stats',
  //         title: 'Analytics',
  //         description: 'Access analytical insights that support effective price planning and pricing decisions.',
  //         links: [
  //           { id: 'analytics-run', label: 'Run Analytics', targetType: 'dashboard', target: '', style: 'outline' },
  //         ],
  //       },
  //     ],
  //   },
  // },
  {
    id: 'blank',
    name: 'Blank landing page',
    description: 'An empty header and one starter module — build the page up from scratch.',
    config: {
      header: { icon: 'dashboard', title: 'My Landing Page', subtitle: 'Choose where to go.' },
      sectionLabel: 'SELECT MODULE',
      accent: '#c8102e',
      modules: [
        {
          id: 'module-1',
          badge: 'MODULE 01',
          icon: 'dashboard',
          title: 'New module',
          description: 'Describe what this module offers.',
          links: [
            { id: 'link-1', label: 'Open', targetType: 'dashboard', target: '', style: 'outline' },
          ],
        },
      ],
    },
  },
];
